import { useRef, useEffect, useCallback, useState } from 'react';
import * as Cesium from 'cesium';
import config from "../../config";
import { usePropertyVisualization } from './usePropertyVisualization';
import { useSceneSettings } from './useSceneSettings';
import { useProfilerHighlighting } from './useProfilerHighlighting';
import { useCameraControl } from './useCameraControl';
import { useViewModes } from './useViewModes';

const VIEWER_STATE_KEY = 'terrain';

const ensureViewerState = (viewer) => {
  if (!viewer) return null;
  if (!viewer[VIEWER_STATE_KEY]) viewer[VIEWER_STATE_KEY] = {};
  return viewer[VIEWER_STATE_KEY];
};

const findTerrainModelIndex = (models, searchTerm) => {
  if (!Array.isArray(models) || !searchTerm) return -1;
  const normalized = searchTerm.toLowerCase();
  return models.findIndex((model) => (model?.name || '').toLowerCase().includes(normalized));
};

const pickDefaultTerrainModel = (models) => {
  if (!Array.isArray(models) || models.length === 0) return null;
  const preferredIndex = findTerrainModelIndex(models, 'Japan Regional Terrain (GSI)');
  const index = preferredIndex >= 0 ? preferredIndex : 0;
  return { model: models[index], index };
};

// Approximate bounding box for Japan (lat 24-46°N, lon 122-154°E)
const JAPAN_BOUNDS = { minLat: 24, maxLat: 46, minLon: 122, maxLon: 154 };

const isLocationInJapan = (cartographic) => {
  const latDeg = Cesium.Math.toDegrees(cartographic.latitude);
  const lonDeg = Cesium.Math.toDegrees(cartographic.longitude);
  return (
    latDeg >= JAPAN_BOUNDS.minLat && latDeg <= JAPAN_BOUNDS.maxLat &&
    lonDeg >= JAPAN_BOUNDS.minLon && lonDeg <= JAPAN_BOUNDS.maxLon
  );
};

const resolveTerrainProvider = async (model) => {
  const factory = model?._creationCommand || model?.creationFunction || model?.command;
  if (typeof factory !== 'function') {
    throw new Error('Terrain model is missing a creation function');
  }
  const provider = await Promise.resolve(factory());
  if (!provider) {
    throw new Error('Terrain provider creation returned no provider');
  }
  return provider;
};

/**
 * Custom hook for managing Cesium viewer initialization and configuration.
 * Orchestrates focused sub-hooks for property visualization, scene settings,
 * profiler highlighting, camera control, and view modes.
 * @param {React.RefObject} containerRef - Reference to the container element
 * @param {string} token - Cesium Ion access token
 * @param {number} assetId - Point cloud asset ID
 * @param {number} assetIdGS - Gaussian splatting asset ID
 */
export const useCesiumViewer = (containerRef, token, assetId, assetIdGS) => {
  // Core refs
  const viewerRef = useRef(null);
  const tilesetRef = useRef(null);
  const gaussianTilesetRef = useRef(null);
  const handlerRef = useRef(null);

  // Viewer lifecycle state
  const [viewerReady, setViewerReady] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [renderErrorKey, setRenderErrorKey] = useState(0);
  const renderErrorAttemptsRef = useRef(0);

  // Expose terrain view models created during initialization
  const imageryProviderViewModelsRef = useRef([]);
  const terrainProviderViewModelsRef = useRef([]);

  // Compose sub-hooks — destructure to keep stable references in dependency arrays
  const propertyVis = usePropertyVisualization(tilesetRef);
  const sceneSettings = useSceneSettings(viewerRef, tilesetRef);
  const profiler = useProfilerHighlighting(viewerRef, tilesetRef);
  const camera = useCameraControl(viewerRef, tilesetRef);
  const {
    currentDisplayMode, setCurrentDisplayMode,
    currentViewMode, setCurrentViewMode,
    splitPosition, setSplitPosition,
    switchDisplayMode, switchViewMode, updateSplitPosition,
  } = useViewModes(viewerRef, tilesetRef, gaussianTilesetRef);

  /**
   * Initializes the Cesium viewer with default configuration
   */
  const initializeViewer = useCallback(() => {
    if (!containerRef.current || !token) {
      console.log('Missing container or token:', {
        container: !!containerRef.current,
        token: !!token
      });
      return null;
    }

    Cesium.Ion.defaultAccessToken = token;
    const imageryModels = Cesium.createDefaultImageryProviderViewModels();
    console.log('Available imagery models:', imageryModels.map((model, index) => ({
      index,
      name: model.name
    })));

    imageryProviderViewModelsRef.current = imageryModels;

    const defaultTerrainModels = Cesium.createDefaultTerrainProviderViewModels();

    const japanTerrainModel = {
      name: 'Japan Regional Terrain (GSI)',
      iconUrl: Cesium.buildModuleUrl('https://avatars.githubusercontent.com/u/4182264?s=200&v=4'),
      tooltip: 'High-resolution terrain for Japan region (Cesium ion 2767062)',
      category: 'Cesium ion',
      creationFunction: () =>
        Cesium.CesiumTerrainProvider.fromIonAssetId(config.japanRegionalTerrain, {
          requestWaterMask: true,
          requestVertexNormals: true,
        })
    };

    let terrainModels = defaultTerrainModels;
    terrainModels.push(japanTerrainModel);
    terrainProviderViewModelsRef.current = terrainModels;

    console.log('Available terrain models:', terrainModels.map((model, index) => ({
      index,
      name: model.name,
      category: model._category || model.category
    })));

    const viewer = new Cesium.Viewer(containerRef.current, {
      animation: false,
      timeline: false,
      sceneModePicker: false,
      homeButton: false,
      baseLayerPicker: false,
      fullscreenButton: false,
      vrButton: false,
      geocoder: false,
      navigationHelpButton: false,
      selectionIndicator: false,
      infoBox: false,
    });

    const viewerState = ensureViewerState(viewer);
    const baseLayerState = viewer ? (viewer.__mirukuru || (viewer.__mirukuru = {})) : null;

    if (terrainModels.length > 0) {
      const selection = pickDefaultTerrainModel(terrainModels);
      if (selection) {
        const { index } = selection;
        if (viewerState) viewerState.terrainIndex = index;
        if (baseLayerState) baseLayerState.terrainIndex = index;
      }
    }

    viewer.cesiumWidget.creditContainer.style.display = "none"

    viewer.scene.globe.enableLighting = false;
    viewer.scene.globe.depthTestAgainstTerrain = true;
    viewer.scene.pickTranslucentDepth = true;

    viewer.scene.useDepthPicking = true;
    viewer.scene.globe.translucency.enabled = false;

    viewer.useBrowserRecommendedResolution = false;

    return viewer;
  }, [containerRef, token]);

  /**
   * Loads a 3D tileset from Cesium Ion
   */
  const loadTileset = useCallback(async (viewer, id, pointSize = 5, eyeDomeLighting = false, isGaussianSplatting = false) => {
    try {
      const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(id);
      console.log(`[loadTileset] asset ${id}: fromIonAssetId resolved, viewer destroyed=${viewer.isDestroyed()}`);

      if (viewer.isDestroyed()) {
        console.warn(`[loadTileset] asset ${id}: viewer destroyed before primitives.add`);
        throw new Error('Viewer was destroyed during tileset loading');
      }
      viewer.scene.primitives.add(tileset);
      await viewer.zoomTo(tileset);

      console.log(`[loadTileset] asset ${id}: zoomTo resolved, viewer destroyed=${viewer.isDestroyed()}`);
      if (viewer.isDestroyed()) {
        console.warn(`[loadTileset] asset ${id}: viewer destroyed after zoomTo`);
        throw new Error('Viewer was destroyed during tileset zoom');
      }

      if (!isGaussianSplatting) {
        // Initialize property ranges from bounding volume
        const elevInfo = propertyVis.initializeTilesetProperties(tileset);

        // Create and attach custom shader
        tileset.customShader = propertyVis.buildCustomShader(pointSize, elevInfo);

        // Configure point cloud shading
        tileset.pointCloudShading.attenuation = true;
        tileset.pointCloudShading.eyeDomeLighting = eyeDomeLighting;
        tileset.pointCloudShading.eyeDomeLightingStrength = sceneSettings.edlStrengthRef.current;
        tileset.pointCloudShading.eyeDomeLightingRadius = sceneSettings.edlRadiusRef.current;
      }

      return tileset;
    } catch (error) {
      console.error('Error loading point cloud tileset:', error);
      if (error?.response) {
        try {
          const response = JSON.parse(error.response);
          if (response?.message?.includes('still being processed')) {
            const processingError = new Error('Asset is still being processed');
            processingError.isProcessing = true;
            throw processingError;
          }
        } catch (parseError) {
          if (parseError.isProcessing) throw parseError;
        }
      }
      throw error;
    }
  }, [propertyVis.initializeTilesetProperties, propertyVis.buildCustomShader, sceneSettings.edlStrengthRef, sceneSettings.edlRadiusRef]);

  /**
   * Sets up click event handlers for the viewer
   */
  const setupClickHandler = useCallback((leftClickHandler, rightClickHandler = null) => {
    const viewer = viewerRef.current;
    if (!viewer || !leftClickHandler) return;

    if (handlerRef.current) {
      handlerRef.current.destroy();
      handlerRef.current = null;
    }

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);

    handler.setInputAction((click) => {
      const position = viewer.scene.pickPosition(click.position);
      if (!position) {
        console.warn('No depth available at this pixel');
        return;
      }
      leftClickHandler(position, click.position);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    if (rightClickHandler) {
      handler.setInputAction((click) => {
        const position = viewer.scene.pickPosition(click.position);
        if (position) {
          rightClickHandler(position, click.position);
        } else {
          rightClickHandler();
        }
      }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
    }

    handlerRef.current = handler;
  }, []);

  /**
   * Cleanup function to destroy viewer and handlers
   */
  const cleanup = useCallback((options = {}) => {
    const { preserveError = false } = options;
    const callerStack = new Error().stack.split('\n').slice(2, 5).join(' | ');
    console.log('[cleanup] called', {
      viewerAlive: !!(viewerRef.current && !viewerRef.current.isDestroyed()),
      hasHandler: !!handlerRef.current,
      hasTileset: !!tilesetRef.current,
      hasGaussian: !!gaussianTilesetRef.current,
      from: callerStack,
    });

    if (handlerRef.current) {
      handlerRef.current.destroy();
      handlerRef.current = null;
    }

    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      try {
        if (gaussianTilesetRef.current && !gaussianTilesetRef.current.isDestroyed()) {
          const removed = viewer.scene.primitives.remove(gaussianTilesetRef.current);
          if (!removed && !gaussianTilesetRef.current.isDestroyed()) {
            gaussianTilesetRef.current.destroy();
          }
        }

        if (tilesetRef.current && !tilesetRef.current.isDestroyed()) {
          const removed = viewer.scene.primitives.remove(tilesetRef.current);
          if (!removed && !tilesetRef.current.isDestroyed()) {
            tilesetRef.current.destroy();
          }
        }
      } catch (error) {
        console.warn('Error during tileset cleanup:', error);
      }
    }

    tilesetRef.current = null;
    gaussianTilesetRef.current = null;

    if (viewer && !viewer.isDestroyed()) {
      try {
        viewer.destroy();
      } catch (error) {
        console.warn('Error during viewer cleanup:', error);
      }
      viewerRef.current = null;
    }

    setViewerReady(false);
    setCurrentDisplayMode('pointcloud');
    setCurrentViewMode('full');
    setSplitPosition(0.5);
    if (!preserveError) {
      setViewerError(null);
    }
    console.log('Cleaning over');
  }, [setCurrentDisplayMode, setCurrentViewMode, setSplitPosition]);

  /**
   * Initialize viewer and load tilesets on mount
   */
  useEffect(() => {
    if (!containerRef.current || !token || (!assetId && !assetIdGS)) {
      console.log('Skipping viewer initialization - missing requirements:', {
        container: !!containerRef.current,
        token: !!token,
        assetId: !!assetId,
        assetIdGS: !!assetIdGS
      });
      return;
    }

    let cancelled = false;

    const initializeAsync = async () => {
      try {
        console.log('Initializing Cesium viewer...');
        const viewer = initializeViewer();
        if (!viewer) {
          console.error('Failed to create viewer');
          return;
        }

        viewerRef.current = viewer;

        // Start terrain loading in parallel with tileset loading
        const terrainModels = terrainProviderViewModelsRef.current;
        const terrainPromise = (async () => {
          if (terrainModels.length === 0) return;
          const selection = pickDefaultTerrainModel(terrainModels);
          if (!selection) return;
          const { model } = selection;
          const viewerState = ensureViewerState(viewer);
          const baseLayerState = viewer.__mirukuru || (viewer.__mirukuru = {});
          try {
            const provider = await resolveTerrainProvider(model);
            if (cancelled || viewer.isDestroyed()) return;
            viewer.terrainProvider = provider;
            if (provider?.readyPromise) {
              provider.readyPromise.catch(err =>
                console.error('Terrain provider failed to finish loading', err)
              );
            }
          } catch (error) {
            console.warn('Failed to load initial terrain provider, falling back to ellipsoid terrain', error);
            if (cancelled || viewer.isDestroyed()) return;
            viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
            const fallbackIndex = findTerrainModelIndex(terrainModels, 'ellipsoid');
            const safeIndex = fallbackIndex >= 0 ? fallbackIndex : 0;
            if (viewerState) viewerState.terrainIndex = safeIndex;
            if (baseLayerState) baseLayerState.terrainIndex = safeIndex;
          }
        })();

        // On render error, trigger a full viewer re-initialization
        viewer.scene.renderError.addEventListener((_scene, error) => {
          if (viewer.isDestroyed()) return;

          try {
            const panel = viewer.container?.querySelector('.cesium-widget-errorPanel');
            if (panel) panel.style.display = 'none';
          } catch { /* ignore */ }

          cancelled = true;

          if (renderErrorAttemptsRef.current >= 3) {
            console.error('[renderError] exceeded threshold, giving up:', error);
            return;
          }
          renderErrorAttemptsRef.current++;
          console.error(`[renderError] attempt ${renderErrorAttemptsRef.current}, triggering re-init:`, error, '\nStack:', error?.stack ?? '(no stack)');
          setRenderErrorKey(k => k + 1);
        });

        // Load point cloud tileset
        console.log('Loading point cloud tileset for asset ID:', assetId);
        if (assetId) {
          const pointCloudTileset = await loadTileset(viewer, assetId, 5, true, false);
          if (cancelled) { console.log('[initializeAsync] cancelled after point-cloud loadTileset'); return; }
          tilesetRef.current = pointCloudTileset;
        }
        console.log('Point cloud tileset loaded successfully');

        // Load Gaussian splatting tileset if available
        if (assetIdGS) {
          try {
            console.log('Loading Gaussian splatting tileset for asset ID:', assetIdGS);
            const gaussianTileset = await loadTileset(viewer, assetIdGS, 5, true, true);
            if (cancelled) { console.log('[initializeAsync] cancelled after gaussian loadTileset'); return; }
            gaussianTilesetRef.current = gaussianTileset;
            if (assetId) {
              gaussianTileset.show = false;
              gaussianTileset.preloadWhenHidden = true;
            } else {
              gaussianTileset.show = true;
              setCurrentDisplayMode('gaussian');
            }
          } catch (error) {
            console.warn('Failed to load Gaussian splatting tileset:', error);
            if (!assetId) {
              throw error;
            }
          }
        }

        if (cancelled) { console.log('[initializeAsync] cancelled before terrain check'); return; }

        // Check tileset location and switch terrain if data is outside Japan
        const primaryTileset = tilesetRef.current || gaussianTilesetRef.current;
        if (primaryTileset) {
          const center = primaryTileset.boundingSphere.center;
          const cartographic = Cesium.Cartographic.fromCartesian(center);
          if (!isLocationInJapan(cartographic)) {
            const terrainModels = terrainProviderViewModelsRef.current;
            const cwIndex = findTerrainModelIndex(terrainModels, 'cesium world terrain');
            if (cwIndex >= 0) {
              try {
                const cwModel = terrainModels[cwIndex];
                const provider = await resolveTerrainProvider(cwModel);
                if (cancelled) return;
                viewer.terrainProvider = provider;
                const viewerState = ensureViewerState(viewer);
                const baseLayerState = viewer.__mirukuru || (viewer.__mirukuru = {});
                if (viewerState) viewerState.terrainIndex = cwIndex;
                if (baseLayerState) baseLayerState.terrainIndex = cwIndex;
                console.log('Data outside Japan — switched to Cesium World Terrain');
              } catch (err) {
                console.warn('Failed to switch to Cesium World Terrain:', err);
              }
            }
          }
        }

        await terrainPromise;
        if (cancelled) return;

        // Let Cesium render one frame with fully-settled state
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (cancelled) return;

        renderErrorAttemptsRef.current = 0;
        setViewerError(null);
        setViewerReady(true);

      } catch (error) {
        if (cancelled) return;
        console.error('Failed to initialize Cesium viewer:', error);
        cleanup({ preserveError: true });
        setViewerError(error);
        setViewerReady(false);
      }
    };

    initializeAsync();

    return () => { cancelled = true; cleanup(); };
  }, [containerRef, token, assetId, assetIdGS, renderErrorKey, initializeViewer, loadTileset, cleanup]);

  return {
    viewer: viewerReady ? viewerRef.current : null,
    tileset: viewerReady ? tilesetRef.current : null,
    gaussianTileset: viewerReady ? gaussianTilesetRef.current : null,
    viewerError,
    setupClickHandler,
    cleanup,

    // View modes
    currentDisplayMode,
    currentViewMode,
    splitPosition,
    switchDisplayMode,
    switchViewMode,
    updateSplitPosition,

    // Scene settings
    updatePointSize: sceneSettings.updatePointSize,
    updateEyeDomeLighting: sceneSettings.updateEyeDomeLighting,
    updateEyeDomeLightingStrength: sceneSettings.updateEyeDomeLightingStrength,
    updateEyeDomeLightingRadius: sceneSettings.updateEyeDomeLightingRadius,
    updateGlobeLighting: sceneSettings.updateGlobeLighting,
    updateAtmosphereRendering: sceneSettings.updateAtmosphereRendering,
    updateTerrainRendering: sceneSettings.updateTerrainRendering,
    updateUseBrowserRecommendationResolution: sceneSettings.updateUseBrowserRecommendationResolution,
    updateSSAO: sceneSettings.updateSSAO,
    updateFXAA: sceneSettings.updateFXAA,
    updateContourLines: sceneSettings.updateContourLines,

    // Camera
    recenterCamera: camera.recenterCamera,
    updateCameraFOV: camera.updateCameraFOV,
    updateCameraMovement: camera.updateCameraMovement,

    // Profiler
    enableProfilerHighlighting: profiler.enableProfilerHighlighting,
    disableProfilerHighlighting: profiler.disableProfilerHighlighting,
    updateProfilerHighlighting: profiler.updateProfilerHighlighting,
    extractProfilerPoints: profiler.extractProfilerPoints,

    // Property visualization
    selectedProperty: propertyVis.selectedProperty,
    classificationFilter: propertyVis.classificationFilter,
    intensityRange: propertyVis.intensityRange,
    elevationRange: propertyVis.elevationRange,
    distanceRange: propertyVis.distanceRange,
    distanceBounds: propertyVis.distanceBounds,
    elevationBounds: propertyVis.elevationBounds,
    updateSelectedProperty: propertyVis.updateSelectedProperty,
    updateClassificationFilter: propertyVis.updateClassificationFilter,
    updateIntensityRange: propertyVis.updateIntensityRange,
    updateElevationRange: propertyVis.updateElevationRange,
    recalibrateElevation: propertyVis.recalibrateElevation,
    updateDistanceRange: propertyVis.updateDistanceRange,
    intensityColormap: propertyVis.intensityColormap,
    updateIntensityColormap: propertyVis.updateIntensityColormap,
    elevationColormap: propertyVis.elevationColormap,
    updateElevationColormap: propertyVis.updateElevationColormap,
    distanceColormap: propertyVis.distanceColormap,
    updateDistanceColormap: propertyVis.updateDistanceColormap,

    // BaseLayer data for custom picker
    imageryProviderViewModels: viewerReady && viewerRef.current ? imageryProviderViewModelsRef.current : [],
    terrainProviderViewModels: viewerReady && viewerRef.current ? terrainProviderViewModelsRef.current : [],
  };
};
